/** @format */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { digitalContentService } from '../../Services/digitalContent.services';

const CONTENT_TYPES = [
  { value: 'image', labelKey: 'DigitalContent-ContentType-Image' },
  { value: 'video', labelKey: 'DigitalContent-ContentType-Video' },
  { value: 'text', labelKey: 'DigitalContent-ContentType-Text' },
];

const UpdateDigitalContent = ({ onSuccess, setResultModal, updateData, disabled }) => {
  const { t: translator } = useTranslation();
  const { register, handleSubmit, errors, watch, setValue } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const [filePreview, setFilePreview] = useState(null);
  const contentType = watch('contentType', updateData.contentType);

  useEffect(() => {
    setValue('contentType', updateData.contentType, { shouldValidate: true });
    setValue('duration', updateData.duration);
    setValue('validFrom', updateData.validFrom ? updateData.validFrom.substring(0, 10) : '');
    setValue('validTo', updateData.validTo ? updateData.validTo.substring(0, 10) : '');
    if (updateData.contentType === 'text') {
      setValue('contentUrl', updateData.content);
    }
  }, [updateData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append('data.contentType', data.contentType);
    formData.append('data.duration', data.duration);
    formData.append('data.validFrom', data.validFrom);
    formData.append('data.validTo', data.validTo);

    if (data.contentFile && data.contentFile[0]) {
      formData.append('data.content', data.contentFile[0]);
    } else if (data.contentUrl) {
      formData.append('data.content', data.contentUrl);
    }

    digitalContentService
      .update(updateData.digitalContentId, formData)
      .then((res) => {
        onSuccess();
        setResultModal({
          show: true,
          title: translator('DigitalContent-Update-SuccessModal-Title'),
          text: translator(res.data.message),
          btnClass: 'btn btn-primary',
        });
      })
      .catch((err) => console.log(err));
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='p-4'
      style={{ border: '1px solid lightgray' }}>
      <h5 className='mb-4 text-center font-weight-bold'>
        {translator('DigitalContent-Update')}
      </h5>

      <fieldset className='scheduler-border'>
        <div className='form-group'>
          <label>{translator('DigitalContent-ContentType')}</label>
          <select
            className='form-control'
            name='contentType'
            ref={register({ required: true })}
            defaultValue={updateData.contentType}
            disabled={disabled}>
            <option value=''>{translator('DigitalContent-ContentType-Select')}</option>
            {CONTENT_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>
                {translator(ct.labelKey)}
              </option>
            ))}
          </select>
          {errors.contentType && errors.contentType.type === 'required' && (
            <p className='text-danger'>{translator('ValidationMsg-RequiredField')}</p>
          )}
        </div>

        {(contentType === 'image' || contentType === 'video') && (
          <div className='form-group'>
            <label>{translator('DigitalContent-ContentFile')}</label>
            {updateData.content && !filePreview && (
              <div className='mb-2'>
                {contentType === 'image' ? (
                  <img src={updateData.content} alt='current' style={{ maxHeight: 100 }} />
                ) : (
                  <video src={updateData.content} style={{ maxHeight: 100 }} controls />
                )}
              </div>
            )}
            <input
              type='file'
              className='form-control-file'
              name='contentFile'
              accept={contentType === 'image' ? 'image/*' : 'video/*'}
              ref={register()}
              onChange={handleFileChange}
              disabled={disabled}
            />
            {filePreview && contentType === 'image' && (
              <img src={filePreview} alt='preview' className='mt-2' style={{ maxHeight: 120 }} />
            )}
            {filePreview && contentType === 'video' && (
              <video src={filePreview} className='mt-2' style={{ maxHeight: 120 }} controls />
            )}
          </div>
        )}

        {contentType === 'text' && (
          <div className='form-group'>
            <label>{translator('DigitalContent-ContentText')}</label>
            <textarea
              className='form-control'
              name='contentUrl'
              rows={4}
              ref={register({ required: true })}
              defaultValue={updateData.content}
              disabled={disabled}
            />
            {errors.contentUrl && errors.contentUrl.type === 'required' && (
              <p className='text-danger'>{translator('ValidationMsg-RequiredField')}</p>
            )}
          </div>
        )}

        <div className='form-group'>
          <label>{translator('DigitalContent-Duration')}</label>
          <input
            type='number'
            className='form-control'
            name='duration'
            min={1}
            ref={register({ required: true, min: 1 })}
            defaultValue={updateData.duration}
            disabled={disabled}
          />
          {errors.duration && errors.duration.type === 'required' && (
            <p className='text-danger'>{translator('ValidationMsg-RequiredField')}</p>
          )}
          {errors.duration && errors.duration.type === 'min' && (
            <p className='text-danger'>{translator('ValidationMsg-MinValue1')}</p>
          )}
        </div>

        <div className='form-row'>
          <div className='form-group col-6'>
            <label>{translator('DigitalContent-ValidFrom')}</label>
            <input
              type='date'
              className='form-control'
              name='validFrom'
              ref={register({ required: true })}
              disabled={disabled}
            />
            {errors.validFrom && errors.validFrom.type === 'required' && (
              <p className='text-danger'>{translator('ValidationMsg-RequiredField')}</p>
            )}
          </div>

          <div className='form-group col-6'>
            <label>{translator('DigitalContent-ValidTo')}</label>
            <input
              type='date'
              className='form-control'
              name='validTo'
              ref={register({ required: true })}
              disabled={disabled}
            />
            {errors.validTo && errors.validTo.type === 'required' && (
              <p className='text-danger'>{translator('ValidationMsg-RequiredField')}</p>
            )}
          </div>
        </div>
      </fieldset>

      <div className='d-flex justify-content-end mt-2'>
        <button className='btn btn-primary' type='submit' disabled={disabled}>
          {translator('Btn-Save')}
        </button>
      </div>
    </form>
  );
};

export default UpdateDigitalContent;
